import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ArrowDown, Search } from 'lucide-react';

const COLUMNS = [
  { key: 'supplier', label: 'Supplier', align: 'left' },
  { key: 'main_category', label: 'Main Category', align: 'left' },
  { key: 'total_spend', label: 'Spend', sortable: true },
  { key: 'spend_percentage', label: '% of Addressable' },
  { key: 'transaction_count', label: 'Transactions', sortable: true },
  { key: 'avg_transaction', label: 'Avg Transaction', sortable: true },
];
const PAGE = 50;

export function TailSupplierTable({ rows, cs }) {
  const [sortKey, setSortKey] = useState('total_spend');
  const [search, setSearch] = useState('');
  const [limit, setLimit] = useState(PAGE);

  const sorted = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q ? rows.filter((r) => r.supplier.toLowerCase().includes(q) || (r.main_category || '').toLowerCase().includes(q)) : rows;
    return [...list].sort((a, b) => b[sortKey] - a[sortKey]);
  }, [rows, sortKey, search]);

  const cell = (r, k) => {
    if (k === 'total_spend' || k === 'avg_transaction') return `${cs}${Math.round(r[k]).toLocaleString()}`;
    if (k === 'spend_percentage') return `${r[k].toFixed(2)}%`;
    return r[k];
  };

  return (
    <Card data-testid="tail-supplier-table">
      <CardHeader className="pb-2 flex flex-row items-center justify-between gap-4">
        <CardTitle className="text-base">Tail Suppliers ({sorted.length})</CardTitle>
        <div className="relative w-64">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input value={search} onChange={(e) => { setSearch(e.target.value); setLimit(PAGE); }} placeholder="Search supplier or category"
            className="h-8 pl-8 text-xs" data-testid="tail-supplier-search" />
        </div>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <>{COLUMNS.map((c) => (
                <th key={c.key} className={`py-1.5 px-3 text-xs font-semibold text-muted-foreground uppercase ${c.align === 'left' ? 'text-left' : 'text-right'}`}>
                  <>{c.sortable ? (
                    <button type="button" onClick={() => setSortKey(c.key)} data-testid={`tail-sort-${c.key}`}
                      className={`inline-flex items-center gap-1 uppercase hover:text-foreground ${sortKey === c.key ? 'text-foreground' : ''}`}>
                      {c.label}{sortKey === c.key && <ArrowDown className="w-3 h-3" />}
                    </button>
                  ) : c.label}</>
                </th>
              ))}</>
            </tr>
          </thead>
          <tbody>
            <>{sorted.slice(0, limit).map((r) => (
              <tr key={r.supplier} className="border-b border-border hover:bg-muted/50 transition-colors" data-testid="tail-supplier-row">
                <>{COLUMNS.map((c) => (
                  <td key={c.key} className={`py-1.5 px-3 ${c.align === 'left' ? 'text-left' : 'text-right font-mono'} ${c.key === 'supplier' ? 'font-medium' : ''}`}>{cell(r, c.key)}</td>
                ))}</>
              </tr>
            ))}</>
          </tbody>
        </table>
        {sorted.length > limit && (
          <button type="button" onClick={() => setLimit(limit + PAGE)} className="mt-3 text-xs text-primary hover:underline" data-testid="tail-show-more">
            Show {Math.min(PAGE, sorted.length - limit)} more
          </button>
        )}
        {sorted.length === 0 && <p className="text-center text-muted-foreground py-6 text-xs">No suppliers match your search.</p>}
      </CardContent>
    </Card>
  );
}
